import React from "react";
import { Box, Button } from "@mui/material";
import {
  ArrowBack,
  ShareOutlined,
  PictureAsPdfOutlined,
  BuildOutlined,
  EditOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";
import { dt } from "../../utils/dt";
import useTranslation from "../../../../shared/hooks/useTranslation";

// ===================================================================
//  COMPONENT
// ===================================================================
interface SrActionBarProps {
  onBack: () => void;
  onShare: () => void;
  onDownloadPdf: () => void;
  onCreateServiceRequest: () => void;
  onEdit: () => void;
  onSolve: () => void;
  isSolved: boolean;
  isPhone: boolean;
  isTabletOrPhone: boolean;
  canEdit: boolean;
  canSolve: boolean;
  canCreateSR: boolean;
}

const SrActionBar = ({
  onBack,
  onShare,
  onDownloadPdf,
  onCreateServiceRequest,
  onEdit,
  onSolve,
  isSolved,
  isPhone,
  isTabletOrPhone,
  canEdit,
  canSolve,
  canCreateSR,
}: SrActionBarProps) => {
  const { translate } = useTranslation("myMachinery");

  // Phone + Tablet: fixed bottom bar
  if (isTabletOrPhone) {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          backgroundColor: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(8px)",
          borderTop: `1px solid ${dt.cardBorder}`,
          padding: isPhone ? "10px 16px" : "12px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        {/* Left: Back */}
        <ActionButton
          icon={<ArrowBack sx={{ fontSize: 16 }} />}
          label={translate("srDetail.backToMachine")}
          onClick={onBack}
          isPhone={isPhone}
        />

        {/* Right: Actions */}
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          <ActionButton
            icon={<ShareOutlined sx={{ fontSize: 16 }} />}
            label=""
            onClick={onShare}
            isPhone={isPhone}
            iconOnly
            tooltip={translate("srDetail.shareReport")}
          />
          <ActionButton
            icon={<PictureAsPdfOutlined sx={{ fontSize: 16 }} />}
            label=""
            onClick={onDownloadPdf}
            isPhone={isPhone}
            iconOnly
            tooltip={translate("srDetail.downloadPdf")}
          />
          {canEdit && (
            <ActionButton
              icon={<EditOutlined sx={{ fontSize: 16 }} />}
              label=""
              onClick={onEdit}
              isPhone={isPhone}
              iconOnly
              tooltip={translate("srDetail.editReport")}
            />
          )}
          {!isSolved && canSolve && (
            <ActionButton
              icon={<CheckCircleOutline sx={{ fontSize: 16 }} />}
              label={translate("markAsSolved")}
              onClick={onSolve}
              isPhone={isPhone}
              primary
            />
          )}
        </Box>
      </Box>
    );
  }

  // Desktop: inline row in centre column
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: "wrap",
        py: 1,
      }}
    >
      {/* Left actions */}
      <ActionButton
        icon={<ArrowBack sx={{ fontSize: 16 }} />}
        label={translate("srDetail.backToMachine")}
        onClick={onBack}
        isPhone={false}
      />

      {/* Right actions */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <ActionButton
          icon={<ShareOutlined sx={{ fontSize: 16 }} />}
          label={translate("srDetail.shareReport")}
          onClick={onShare}
          isPhone={false}
        />
        <ActionButton
          icon={<PictureAsPdfOutlined sx={{ fontSize: 16 }} />}
          label={translate("srDetail.downloadPdf")}
          onClick={onDownloadPdf}
          isPhone={false}
        />
        {canCreateSR && (
          <ActionButton
            icon={<BuildOutlined sx={{ fontSize: 16 }} />}
            label={translate("createServiceRequest")}
            onClick={onCreateServiceRequest}
            isPhone={false}
          />
        )}
        {canEdit && (
          <ActionButton
            icon={<EditOutlined sx={{ fontSize: 16 }} />}
            label={translate("srDetail.editReport")}
            onClick={onEdit}
            isPhone={false}
          />
        )}
        {!isSolved && canSolve && (
          <ActionButton
            icon={<CheckCircleOutline sx={{ fontSize: 16 }} />}
            label={translate("markAsSolved")}
            onClick={onSolve}
            isPhone={false}
            primary
          />
        )}
      </Box>
    </Box>
  );
};

// ===================================================================
//  ACTION BUTTON
// ===================================================================
const ActionButton = ({
  icon,
  label,
  onClick,
  isPhone,
  primary = false,
  iconOnly = false,
  tooltip,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isPhone: boolean;
  primary?: boolean;
  iconOnly?: boolean;
  tooltip?: string;
}) => (
  <Button
    variant={primary ? "contained" : "text"}
    size="small"
    startIcon={iconOnly ? undefined : icon}
    onClick={onClick}
    title={tooltip}
    sx={{
      fontFamily: dt.font,
      fontSize: isPhone ? "12px" : "13px",
      fontWeight: 600,
      textTransform: "none",
      borderRadius: dt.radiusPill,
      px: iconOnly ? 1 : isPhone ? 1.5 : 2,
      py: 0.75,
      minWidth: iconOnly ? "36px" : "auto",
      minHeight: "34px",
      ...(primary
        ? {
            background:
              "linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)",
            color: "#FFFFFF",
            boxShadow: "0 2px 8px rgba(39,174,96,0.25)",
            "&:hover": {
              background:
                "linear-gradient(135deg, #219A52 0%, #27AE60 100%)",
              boxShadow: "0 4px 14px rgba(39,174,96,0.35)",
            },
          }
        : {
            color: dt.textSecondary,
            "&:hover": {
              backgroundColor: "rgba(52,152,219,0.06)",
              color: dt.blue,
            },
          }),
    }}
  >
    {iconOnly ? icon : label}
  </Button>
);

export default SrActionBar;
